import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface WorkOrder {
  id: string;
  company_id: string;
  vehicle_id: string;
  order_number: number;
  plan_id?: string;
  provider_id?: string;
  description?: string;
  status: string;
  opened_at: string;
  closed_at?: string;
  total_cost?: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  fleet_vehicles?: {
    id: string;
    plate: string;
    make?: string;
    model?: string;
  };
  fleet_providers?: {
    id: string;
    name: string;
    type: string;
  } | null;
  fleet_maint_plans?: {
    id: string;
    name: string;
    type: string;
  };
}

export function formatWorkOrderNumber(orderNumber: number): string {
  return `OS-${String(orderNumber).padStart(4, '0')}`;
}

export function useWorkOrders(companyId?: string) {
  return useQuery({
    queryKey: ["workOrders", companyId],
    queryFn: async () => {
      console.log('🔧 useWorkOrders: Fetching work orders for company:', companyId);
      
      if (!companyId) {
        console.warn('⚠️ useWorkOrders: No companyId provided, returning empty array');
        return [];
      }

      try {
        // Hotfix: Remover embeddings de fleet_providers e fleet_maint_plans
        // até que as FKs sejam criadas no banco
        const { data, error } = await supabase
          .from("fleet_work_orders")
          .select(`
            *,
            fleet_vehicles(id, plate, make, model)
          `)
          .eq("company_id", companyId)
          .order("order_number", { ascending: false });
        
        if (error) {
          console.error('❌ useWorkOrders: Error fetching work orders:', error);
          throw error;
        }

        console.log('✅ useWorkOrders: Found work orders:', data?.length || 0, data);
        return data || [];
      } catch (error) {
        console.error('❌ useWorkOrders: Failed to fetch work orders:', error);
        throw error;
      }
    },
    enabled: !!companyId,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

export function useCreateWorkOrder(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderData: Omit<WorkOrder, 'id' | 'created_at' | 'updated_at' | 'company_id' | 'fleet_vehicles' | 'fleet_providers' | 'fleet_maint_plans'> & { _items?: Array<{ description: string; quantity: number; unit_price: number }> }) => {
      // Separar itens do restante dos dados
      const { _items, ...workOrderData } = orderData;
      
      const { data, error } = await supabase
        .from("fleet_work_orders")
        .insert([{ ...workOrderData, company_id: companyId }])
        .select()
        .single();
      
      if (error) throw error;
      
      // Salvar itens se houver
      if (_items && _items.length > 0) {
        const itemsToInsert = _items.map(item => ({
          work_order_id: data.id,
          company_id: companyId,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price
        }));
        
        const { error: itemsError } = await supabase
          .from('fleet_work_order_items')
          .insert(itemsToInsert);
        
        if (itemsError) {
          console.error('Error inserting work order items:', itemsError);
          // Não falhar a operação por causa dos itens
        }
      }
      
      // Se a OS foi criada a partir de um plano, verificar e ativar o plano se estiver pendente
      if (data.plan_id) {
        const { data: planData } = await supabase
          .from("fleet_maint_plans")
          .select('id, status')
          .eq('id', data.plan_id)
          .eq('company_id', companyId)
          .single();
        
        // Se o plano está com status 'pending', ativar automaticamente
        if (planData && planData.status === 'pending') {
          await supabase
            .from("fleet_maint_plans")
            .update({ status: 'active' })
            .eq('id', data.plan_id)
            .eq('company_id', companyId);
        }
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrders", companyId] });
      queryClient.invalidateQueries({ queryKey: ["work-order-items"] });
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans", companyId] });
      toast({
        title: "Sucesso",
        description: "Ordem de serviço criada com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error creating work order:", error);
      toast({
        title: "Erro",
        description: "Erro ao criar ordem de serviço.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateWorkOrder(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, _items, ...orderData }: Partial<WorkOrder> & { id: string; _items?: Array<{ tempId: string; description: string; quantity: number; unit_price: number }> }) => {
      // Buscar OS atual antes de atualizar (para verificar plan_id)
      const { data: currentOrder, error: fetchError } = await supabase
        .from("fleet_work_orders")
        .select("plan_id, status")
        .eq("id", id)
        .eq("company_id", companyId)
        .single();
      
      if (fetchError) throw fetchError;

      // Atualizar OS
      const { data, error } = await supabase
        .from("fleet_work_orders")
        .update(orderData)
        .eq("id", id)
        .eq("company_id", companyId)
        .select()
        .single();
      
      if (error) throw error;

      // Atualizar itens se foram passados
      if (_items !== undefined) {
        // Deletar todos os itens existentes e inserir novos
        await supabase
          .from('fleet_work_order_items')
          .delete()
          .eq('work_order_id', id)
          .eq('company_id', companyId);
        
        if (_items.length > 0) {
          const itemsToInsert = _items.map(item => ({
            work_order_id: id,
            company_id: companyId,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price
          }));
          
          const { error: itemsError } = await supabase
            .from('fleet_work_order_items')
            .insert(itemsToInsert);
          
          if (itemsError) {
            console.error('Error updating work order items:', itemsError);
          }
        }
      }

      // Se mudou para 'completed' e tem plano vinculado, atualizar next_execution_km
      if (orderData.status === 'completed' && currentOrder.plan_id && currentOrder.status !== 'completed') {
        console.log('🔄 OS completada, atualizando plano de manutenção...');
        
        const { data: plan, error: planError } = await supabase
          .from('fleet_maint_plans')
          .select('periodicity_km, next_execution_km')
          .eq('id', currentOrder.plan_id)
          .single();
        
        if (!planError && plan && plan.periodicity_km) {
          const newNextKm = (plan.next_execution_km || 0) + plan.periodicity_km;
          
          const { error: updateError } = await supabase
            .from('fleet_maint_plans')
            .update({
              next_execution_km: newNextKm,
              updated_at: new Date().toISOString()
            })
            .eq('id', currentOrder.plan_id);
          
          if (!updateError) {
            console.log(`✅ Plano atualizado: next_execution_km = ${newNextKm} km`);
          } else {
            console.error('❌ Erro ao atualizar plano:', updateError);
          }
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrders", companyId] });
      queryClient.invalidateQueries({ queryKey: ["work-order-items"] });
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans", companyId] });
      toast({
        title: "Sucesso",
        description: "Ordem de serviço atualizada com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error updating work order:", error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar ordem de serviço.",
        variant: "destructive",
      });
    },
  });
}

export function useCloseWorkOrder(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, totalCost }: { id: string; totalCost?: number }) => {
      // 1. Buscar a OS (sem join, apenas plan_id)
      const { data: workOrder, error: fetchError } = await supabase
        .from("fleet_work_orders")
        .select("plan_id")
        .eq("id", id)
        .single();
      
      if (fetchError) throw fetchError;

      // 2. Fechar a OS
      const { data, error } = await supabase
        .from("fleet_work_orders")
        .update({ 
          status: 'closed', 
          closed_at: new Date().toISOString(),
          ...(totalCost && { total_cost: totalCost })
        })
        .eq("id", id)
        .eq("company_id", companyId)
        .select()
        .single();
      
      if (error) throw error;

      // 3. Se a OS está vinculada a um plano, verificar se é corretivo
      if (workOrder?.plan_id) {
        const { data: plan, error: planFetchError } = await supabase
          .from("fleet_maint_plans")
          .select("type")
          .eq("id", workOrder.plan_id)
          .single();
        
        // Se o plano é corretivo, marcar como concluído
        if (!planFetchError && plan?.type === 'corrective') {
          const { error: planUpdateError } = await supabase
            .from("fleet_maint_plans")
            .update({ status: "completed" })
            .eq("id", workOrder.plan_id);
          
          if (planUpdateError) console.error("Erro ao concluir plano:", planUpdateError);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrders", companyId] });
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans", companyId] });
      toast({
        title: "Sucesso",
        description: "Ordem de serviço fechada com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error closing work order:", error);
      toast({
        title: "Erro",
        description: "Erro ao fechar ordem de serviço.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteWorkOrder(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase
        .from("fleet_work_orders")
        .delete()
        .eq("id", orderId)
        .eq("company_id", companyId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workOrders", companyId] });
      toast({
        title: "Sucesso",
        description: "Ordem de serviço excluída com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error deleting work order:", error);
      toast({
        title: "Erro",
        description: "Erro ao excluir ordem de serviço.",
        variant: "destructive",
      });
    },
  });
}